(() => {
  if (customElements.get('collection-launch')) return;

  // Require a calendar-valid ISO timestamp with an explicit zone. Never use the
  // shopper's time zone to interpret a merchant's launch time.
  const parseLaunchTime = (value) => {
    const parts = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})(Z|[+-]\d{2}:\d{2})$/.exec(value);
    if (!parts) return NaN;
    const [, year, month, day, hour, minute, second = '00', zone] = parts;
    const date = new Date(`${year}-${month}-${day}T00:00:00Z`);
    if (
      date.getUTCFullYear() !== Number(year)
      || date.getUTCMonth() + 1 !== Number(month)
      || date.getUTCDate() !== Number(day)
      || Number(hour) > 23 || Number(minute) > 59 || Number(second) > 59
      || (zone !== 'Z' && (Number(zone.slice(1, 3)) > 14 || Number(zone.slice(4)) > 59))
    ) return NaN;
    return Date.parse(value);
  };

  class CollectionLaunch extends HTMLElement {
    connectedCallback() {
      this.endTime = parseLaunchTime(this.dataset.launchAt || '');
      this.units = ['days', 'hours', 'minutes', 'seconds'].map((unit) => this.querySelector(`[data-launch-${unit}]`));
      this.onVisibility = () => {
        this.stopTimer();
        this.update();
        this.startTimer();
      };
      document.addEventListener('visibilitychange', this.onVisibility);
      this.update();
      this.startTimer();
    }

    disconnectedCallback() {
      this.stopTimer();
      document.removeEventListener('visibilitychange', this.onVisibility);
    }

    stopTimer() {
      window.clearInterval(this.timer);
      this.timer = null;
    }

    startTimer() {
      if (!document.hidden && Number.isFinite(this.endTime) && Date.now() < this.endTime && !this.hidden) {
        this.timer = window.setInterval(() => this.update(), 1000);
      }
    }

    update() {
      const designMode = this.dataset.designMode === 'true';
      if (this.dataset.enabled !== 'true' && !designMode) {
        this.hidden = true;
        this.stopTimer();
        return;
      }

      if (!Number.isFinite(this.endTime)) {
        // Invalid/unfinished schedules must not advertise a collection as live.
        this.hidden = !designMode;
        const note = this.querySelector('[data-launch-editor-note]');
        if (note) note.textContent = 'Set a valid launch date and time, including the UTC offset (for example, 2026-10-01T10:00:00-05:00). This section stays hidden on the storefront until then.';
        this.stopTimer();
        return;
      }

      const live = Date.now() >= this.endTime;
      const wasUpcoming = this.dataset.state === 'upcoming';
      this.dataset.state = live ? 'live' : 'upcoming';
      this.hidden = live && this.dataset.afterLaunch === 'hide' && !designMode;
      const activeElement = document.activeElement;
      const focusInside = this.contains(activeElement);

      this.querySelectorAll('[data-launch-upcoming]').forEach((element) => { element.hidden = live; });
      this.querySelectorAll('[data-launch-live]').forEach((element) => { element.hidden = !live; });
      const signup = this.querySelector('[data-launch-signup]');
      const shop = this.querySelector('[data-launch-shop]');
      if (signup) signup.hidden = live && this.dataset.afterLaunch !== 'signup';
      if (shop) shop.hidden = !live || this.dataset.afterLaunch !== 'shop';

      if (live) {
        this.stopTimer();
        if (wasUpcoming && !this.hidden) {
          const announcement = this.querySelector('[data-launch-announcement]');
          if (announcement) announcement.textContent = 'The collection is now live.';
          if (focusInside && signup?.hidden && signup.contains(activeElement)) {
            this.querySelector('[data-launch-shop-link]')?.focus({ preventScroll: true });
          }
        }
        return;
      }

      const remaining = Math.max(0, Math.ceil((this.endTime - Date.now()) / 1000));
      const values = [Math.floor(remaining / 86400), Math.floor(remaining % 86400 / 3600), Math.floor(remaining % 3600 / 60), remaining % 60];
      this.units.forEach((element, index) => {
        if (element) element.textContent = String(values[index]).padStart(2, '0');
      });
    }
  }

  customElements.define('collection-launch', CollectionLaunch);
})();
